import { useDashboardFederationsControllerAvailableByPublicId } from "@/api/__generated__/dashboard-federations/dashboard-federations";
import { useCurrentOrganizationContext, useCurrentPlatformContext } from "@/contexts";
import { mapToAvailableFederation } from "@/mappers";

/**
 * The licences module flag of the organization in scope — the single answer to
 * "does this organization use Tournated licences at all?".
 *
 * A present organization's own `enableLicenses` wins outright, so an
 * organization with licensing off keeps every licence surface hidden. In
 * platform-only mode (no current organization) the platform's data-owner
 * federation stands in as the owning organization — the same fallback
 * `useCreateOwnerOrganization` makes. The slim `dataOwner` ref on the platform
 * record does not carry `enableLicenses`, so it is read from the data-owner
 * federation's `available` record; that request is gated to platform-only mode
 * so it never fires while a real organization is active.
 *
 * Consumed by the sidebar filter (Licenses / Issued licenses items) and by the
 * ranking-category eligibility form, which hides the licence restriction for
 * organizations that have no licences.
 */
export const useIsLicensesEnabled = (): boolean => {
  const { organization, isPlatformOnly } = useCurrentOrganizationContext();
  const { platform: currentPlatform } = useCurrentPlatformContext();

  const dataOwnerId = currentPlatform?.dataOwner?.id ?? null;
  const { data: dataOwnerData } = useDashboardFederationsControllerAvailableByPublicId(
    dataOwnerId ?? "",
    currentPlatform ? { platformId: currentPlatform.id } : undefined,
    { query: { enabled: isPlatformOnly && dataOwnerId !== null } }
  );
  const dataOwnerFederation = dataOwnerData ? mapToAvailableFederation(dataOwnerData) : null;

  return organization?.enableLicenses ?? dataOwnerFederation?.enableLicenses ?? false;
};
